import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareText, Mail, Phone, Linkedin, Instagram, Facebook } from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-emerald-800 text-white py-12">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-6">
              <MessageSquareText className="w-8 h-8" />
              <span className="text-xl font-bold">Account Services</span>
            </Link>
            
            <div className="mb-6">
              <h3 className="font-semibold mb-2">APPORT SOFTWARE SOLUTIONS PRIVATE LIMITED</h3>
              <p className="text-gray-200">
                8th Floor, 8-B, Sagar Sangeet, Colaba<br />
                Post Office, Mumbai,<br />
                Mumbai City, Maharashtra, 400005<br />
                CIN: U72900MH2021PTC354389
              </p>
            </div>

            {/* Sales Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-2">SALES</h3>
              <div className="flex items-center space-x-2 mb-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:sales@getsmsotp.com" className="hover:text-gray-200">sales@getsmsotp.com</a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <a href="tel:+917977814605" className="hover:text-gray-200">+91 7977 814 605</a>
              </div>
            </div>

            {/* Support Section */}
            <div>
              <h3 className="text-sm font-semibold mb-2">SUPPORT</h3>
              <div className="flex items-center space-x-2 mb-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:support@getsmsotp.com" className="hover:text-gray-200">support@getsmsotp.com</a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <a href="tel:+918356849474" className="hover:text-gray-200">+91 8356 849 474</a>
              </div>
            </div>
          </div>

          {/* Product Section */}
          <div>
            <h3 className="text-lg font-medium mb-4">PRODUCT</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/pricing" className="hover:text-gray-200">
                  WhatsApp API and DoubleTick Pricing
                </Link>
              </li>
              <li>
                <Link to="/chat-link" className="hover:text-gray-200">
                  WhatsApp Chat Link Generator
                </Link>
              </li>
              <li>
                <Link to="/qr-code" className="hover:text-gray-200">
                  WhatsApp QR Code Generator
                </Link>
              </li>
              <li>
                <Link to="/partner" className="hover:text-gray-200">
                  Partner with us
                </Link>
              </li>
              <li>
                <Link to="/team-inbox" className="hover:text-gray-200">
                  WhatsApp Team Inbox
                </Link>
              </li>
              <li>
                <Link to="/enterprise" className="hover:text-gray-200">
                  WhatsApp Enterprise
                </Link>
              </li>
              <li>
                <Link to="/flows" className="hover:text-gray-200">
                  WhatsApp Flows
                </Link>
              </li>
              <li>
                <Link to="/chatbot" className="hover:text-gray-200">
                  Whatsapp Chatbot
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Section */}
          <div>
            <h3 className="text-lg font-medium mb-4">RESOURCES</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/blog" className="hover:text-gray-200">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/webinar" className="hover:text-gray-200">
                  WhatsApp API Strategy Webinar
                </Link>
              </li>
              <li>
                <Link to="/developer-api" className="hover:text-gray-200">
                  Developer API
                </Link>
              </li>
              <li>
                <Link to="/business-api" className="hover:text-gray-200">
                  WhatsApp Business API
                </Link>
              </li>
              <li>
                <Link to="/docs" className="hover:text-gray-200">
                  Help & Documentation
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Section */}
          <div>
            <h3 className="text-lg font-medium mb-4">COMPANY</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="hover:text-gray-200">
                  About us
                </Link>
              </li>
              <li>
                <Link to="/careers" className="hover:text-gray-200">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-gray-200">
                  Terms and Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-gray-200">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/refund" className="hover:text-gray-200">
                  Refund and Cancellations
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-gray-200">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/demo" className="hover:text-gray-200">
                  Book a demo
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Mobile App Section */}
        <div className="mt-12 border-t border-emerald-700 pt-8">
          <h3 className="text-lg font-medium mb-4">GET THE APP</h3>
          <p className="mb-4">Download the app on your mobile to get the power of Account Services on the go</p>
          <div className="flex space-x-4">
            <a href="#" className="hover:opacity-90">
              <img src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-example-preferred.png" 
                   alt="Download on the App Store" 
                   className="h-10" />
            </a>
            <a href="#" className="hover:opacity-90">
              <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                   alt="Get it on Google Play" 
                   className="h-10" />
            </a>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="mt-12 border-t border-emerald-700 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p>© {currentYear} Account Services. All rights reserved.</p>
          <p className="mt-4 md:mt-0">Proudly made in 🇮🇳 India, with ❤️</p>
          <div className="flex space-x-4 mt-4 md:mt-0">
            <a href="#" className="hover:text-gray-200">
              <Linkedin className="w-6 h-6" />
            </a>
            <a href="#" className="hover:text-gray-200">
              <Instagram className="w-6 h-6" />
            </a>
            <a href="#" className="hover:text-gray-200">
              <Facebook className="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;